import { ciMoneyNumber, negotiationPotential } from './ci-indicators';

export interface CiMetric {
  label: string;
  value: string;
}

export type CiPropertyType = 'Primary' | 'Resale' | 'Plots' | 'Independent House';

export interface CiReport {
  propertyType?: CiPropertyType;
  propertyName?: string;
  verdict?: string;
  rentalRating?: string;
  negotiationRating?: string;
  analystNote?: string;
  assessmentMetrics?: CiMetric[];
  adjustmentBreakdown?: {
    base: CiMetric;
    final: CiMetric;
    total?: string;
    rows: { label: string; percentage: string; reason?: string }[];
  };
  microMarket?: string;
  directTransactionHistoryLimited?: boolean;
  comparableProjects?: { name: string; location: string; reference: string; psf: string; source?: string; transactions?: { date: string; source: string; psf: string }[] }[];
  investorIntelligence?: CiMetric[];
  investmentScores?: { label: string; score: number }[];
  investmentWealth?: CiMetric[];
  transactions?: { date: string; area: string; price: string; psf: string; source?: string }[];
  transactionAreaEstimated?: boolean;
  summary: string;
  tags: string[];
  pricing: CiMetric[];
  valuation: CiMetric[];
  offers: CiMetric[];
  benchmarkExplanation?: {
    methodology?: string;
    area?: string;
    adjustment?: string;
    benchmarkValue?: string;
  };
  analysis?: {
    askingPrice?: number;
    fairValue?: number;
    suggestedOffer?: number;
    closeLow?: number;
    closeHigh?: number;
    uds?: number;
    difference?: number;
    annualRent?: number;
  };
}

const money = (value: unknown): string => {
  const number = ciMoneyNumber(value);
  if (number == null) return 'Not available';
  return Number.isFinite(number)
    ? `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(number)}`
    : 'Not available';
};

// Ignore empty aliases so richer evidence fields can supply the display value.
function evidenceText(...values: unknown[]): string {
  return (values.find((value): value is string => typeof value === 'string' && !!value.trim()) as string | undefined) ?? 'Not available';
}

function mapTransaction(row: any, calculator: boolean) {
  const rate = row.psf ?? row.ratePerSqft ?? row.pricePerSqft ?? (calculator ? row.price : undefined);
  const total = row.transactionValue ?? row.totalPrice ?? row.transactionAmount ?? (!calculator ? row.price : undefined);
  const displayMoney = (value: unknown) => typeof value === 'string' && value.trim() && ciMoneyNumber(value) == null ? value : money(value);
  return {
    date: transactionDate(row.date ?? row.transactionDate ?? row.registrationDate),
    source: evidenceText(row.source, row.transactionSource),
    area: row.area ?? row.areaSqft ?? 'Not available',
    price: displayMoney(total),
    psf: displayMoney(rate),
  };
}

function cleanCiLabel(value: string): string {
  return value.replace(/[🔵🟠🟡🟢🔴🟣🟤⚫⚪●]/gu, '').trim();
}

// Calculator percentages are ratios; website-ready percentages are already formatted.
const percent = (value: unknown): string => {
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^[-+]?\d+(?:\.\d+)?$/.test(value.trim()))) return 'Not available';
  const number = Number(value);
  return Number.isFinite(number) ? `${(number * 100).toFixed(1)}%` : 'Not available';
};

export function mapCiReport(property: any): CiReport | null {
  const ci = [property?.cipIntelligence, property?.ciIntelligence, property?.ciPublicReport,
    property?.ciRecord, property?.ciCalculation, property?.ci].find(candidate => {
      if (!candidate || typeof candidate !== 'object' || candidate.status === 'DRAFT') return false;
      const propertyId = candidate.propertyId?._id ?? candidate.propertyId;
      return !propertyId || !property?._id || propertyId === property._id;
    });
  if (!ci || typeof ci !== 'object' || ci.status === 'DRAFT') return null;
  const metric = (label: string, value: unknown): CiMetric => ({
    label, value: value == null || value === '' ? 'Not available' : String(value),
  });
  const propertyType = resolveCiPropertyType(ci, property);
  const finish = (report: CiReport) => tailorCiReport(report, propertyType, ci, property);
  if ('pricing' in ci || 'offerGuidance' in ci) {
    const p = ci.pricing ?? {};
    const v = ci.valuation ?? {};
    const o = ci.offerGuidance ?? {};
    return finish({
      transactions: (Array.isArray(ci.transactions) ? ci.transactions : Array.isArray(ci.lastFiveTransactions) ? ci.lastFiveTransactions : [])
        .filter((row: any) => row && typeof row === 'object' && row.source !== 'Land rate benchmark')
        .map((row: any) => mapTransaction(row, !Array.isArray(ci.transactions)))
        .sort((a: any, b: any) => transactionTime(b.date) - transactionTime(a.date)),
      summary: ci.verdict?.summary ?? '',
      benchmarkExplanation: {
        methodology: typeof ci.benchmarkLogicUsed === 'string' ? ci.benchmarkLogicUsed : undefined,
      },
      tags: [...(Array.isArray(ci.trophyTags) ? ci.trophyTags : [ci.trophyTags]), ...(ci.verdict?.badges ?? []).map((badge: any) => badge.label)].filter((tag: unknown) => typeof tag === 'string' && !!tag),
      pricing: [
        metric('Last Transacted PSF', p.lastAvgTransacted),
        metric('Last 5 Transactions Avg PSF', p.last5TransactionsAvg),
        metric('Last Transacted Date', lastTransactedDate(p.lastTransactedDate)),
        metric('UDS Current Rate PSF', p.udsCurrentRate),
        metric('Benchmark PSF', p.benchmarkPsf),
        metric('Revised Benchmark PSF', p.revisedBenchmarkPsf),
        metric('Ask vs Benchmark', p.askVsBenchmark != null && p.askVsBenchmark !== '' ? `${p.askVsBenchmark} ${p.askVsBenchmarkDirection ?? ''}`.trim() : null),
      ],
      valuation: [
        metric('Adjusted Current Value (UDS)', v.adjustedCurrentValue),
        metric('Monthly Rent', v.avgRentMonth),
        metric('Annual Rent', v.avgRentPA),
        metric('Rental Yield', v.rentalYield),
        metric('Rental Strength', v.rentalYieldLabel ?? v.rentalStrength),
        metric('UDS Support vs Close High', v.udsSupport),
      ],
      offers: [
        metric('Suggested Offer PSF', o.suggestedOfferPsf),
        metric('Close PSF - Low', o.closePsfLow),
        metric('Close PSF - High', o.closePsfHigh),
        metric('Suggested Offer Value', o.suggestedOfferValue),
        metric('Likely Close - Low', o.likelyCloseLow),
        metric('Likely Close - High', o.likelyCloseHigh),
      ],
    });
  }
  if (ci.benchmarkPsf == null && ci.revisedBenchmark == null && !ci.adjustedVerdict && !ci.landAssessment) return null;
  return finish({
    transactions: (Array.isArray(ci.lastFiveTransactions) ? ci.lastFiveTransactions : [])
      .filter((row: any) => row && typeof row === 'object' && row.source !== 'Land rate benchmark')
      .map((row: any) => mapTransaction(row, true))
      .sort((a: any, b: any) => transactionTime(b.date) - transactionTime(a.date)),
    transactionAreaEstimated: false,
    benchmarkExplanation: {
      methodology: typeof ci.benchmarkLogicUsed === 'string' ? ci.benchmarkLogicUsed : undefined,
      area: Number(ci.size) > 0 && Number.isFinite(Number(ci.size))
        ? Number(ci.size).toLocaleString('en-IN') : undefined,
      adjustment: ci.totalChecklistWeightagePercent == null ? undefined : percent(ci.totalChecklistWeightagePercent),
      benchmarkValue: Number(ci.size) > 0 && Number(ci.benchmarkPsf) > 0
        ? money(Number(ci.size) * Number(ci.benchmarkPsf)) : undefined,
    },
    analysis: {
      askingPrice: ci.quotedUnitPrice,
      fairValue: Number.isFinite(Number(ci.size)) && Number(ci.size) > 0 && (ciMoneyNumber(ci.benchmarkPsf) ?? 0) > 0
        ? Number(ci.size) * ciMoneyNumber(ci.benchmarkPsf)! : undefined,
      suggestedOffer: ci.suggestedOfferValue,
      closeLow: ci.likelyCloseValueLow,
      closeHigh: ci.likelyCloseValueHigh,
      uds: ci.uds,
      difference: ci.askVsBenchmarkPercent,
      annualRent: ci.averageRentPa,
    },
    summary: ci.verdictDescription || ci.circledIntelligenceVerdict || ci.adjustedVerdict || '',
    tags: [ci.trophyOverride ? 'Trophy Asset' : '', ci.adjustedVerdict].filter(Boolean),
    pricing: [
      metric('Last Transacted PSF', money(ci.lastTransactionPsf)),
      metric('Last 5 Transactions Avg PSF', money(ci.last5TransactionsAvgPsf)),
      metric('Last Transacted Date', lastTransactedDate(ci.dateOfLastTransactedPrice)),
      metric('UDS Current Rate PSF', money(ci.currentUdsRatePerSqft)),
      metric('Benchmark PSF', money(ci.benchmarkPsf)),
      metric('Revised Benchmark PSF', money(ci.revisedBenchmark)),
      metric('Ask vs Benchmark', percent(ci.askVsBenchmarkPercent)),
    ],
    valuation: [
      metric('Adjusted Current Value (UDS)', money(ci.adjustedCurrentValueOfUds)),
      metric('Monthly Rent', money(ci.averageRentPerMonth)),
      metric('Annual Rent', money(ci.averageRentPa)),
      metric('Rental Yield', percent(ci.rentalYield)),
      metric('Rental Strength', ci.rentalYieldLabel ?? ci.rentalStrength),
      metric('UDS Support vs Close High', percent(ci.udsSupportPercent)),
    ],
    offers: [
      metric('Suggested Offer PSF', money(ci.suggestedOfferPsf)),
      metric('Close PSF - Low', money(ci.closePsfLow)),
      metric('Close PSF - High', money(ci.closePsfHigh)),
      metric('Suggested Offer Value', money(ci.suggestedOfferValue)),
      metric('Likely Close - Low', money(ci.likelyCloseValueLow)),
      metric('Likely Close - High', money(ci.likelyCloseValueHigh)),
    ],
  });
}

function lastTransactedDate(value: unknown): string {
  if (typeof value !== 'string') return 'Not available';
  // Reports can include a source label before the date, e.g. "Land rate benchmark – 08 Oct 2026".
  const date = value.match(/\b\d{4}-\d{2}-\d{2}(?=T|\b)/)?.[0]
    ?? value.match(/\b\d{1,2}\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{4}\b/i)?.[0];
  return date ? transactionDate(date) : 'Not available';
}

function transactionTime(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function transactionDate(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return 'Not available';
  if (!/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return value;
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : 'Not available';
}


export function resolveCiPropertyType(ci: any, property: any): CiPropertyType | undefined {
  for (const value of [ci.propertyCategory, ci.propertyType, ci.ciPropertyType, property.ciPropertyType, property.propertyType, property.propertyCategory, property.listingType]) {
    const type = typeof value === 'string' ? value.toLowerCase().replace(/[^a-z]/g, '') : '';
    if (['primary', 'primaryproject'].includes(type)) return 'Primary';
    if (['resale', 'resaleproperty'].includes(type)) return 'Resale';
    if (['plot', 'plots', 'land', 'plotland'].includes(type)) return 'Plots';
    if (['independenthouse', 'independent', 'plotindependenthouse'].includes(type)) return 'Independent House';
  }
  if (ci.landAssessment) return ci.landAssessment.builtUpArea != null ? 'Independent House' : 'Plots';
  if ((ci.lastFiveTransactions ?? []).some((row: any) => row?.source === 'Land rate benchmark')) return /house|villa/i.test(ci.configuration ?? property.propertyType ?? '') ? 'Independent House' : 'Plots';
  if (ci.listingUnitName) return 'Resale';
  return undefined;
}

function tailorCiReport(report: CiReport, propertyType: CiPropertyType | undefined, ci: any, property: any): CiReport {
  report.propertyType = propertyType;
  if (ci.pricing || ci.offerGuidance) {
    const uds = ci.uds ?? ci.valuation?.udsArea;
    if (uds != null && uds !== '' && Number.isFinite(Number(uds))) report.analysis = { ...report.analysis, uds: Number(uds) };
  }
  report.propertyName = ci.propertyName ?? property.name;
  const market = ci.microMarket ?? property.microMarket;
  report.microMarket = typeof market === 'string' ? market : market?.micromarketName ?? market?.cluster;
  const source = ci.benchmarkSource ?? ci.benchmarking?.source;
  const comparisonBased = propertyType === 'Primary' || source === 'similar-listings' || source === 'anchor';
  report.directTransactionHistoryLimited = ci.directTransactionHistoryLimited === true || comparisonBased;
  const comparables = ci.comparableProjects ?? ci.comparables ?? ci.benchmarking?.similarProperties;
  report.comparableProjects = (Array.isArray(comparables) ? comparables : [])
    .filter((row: any) => row && typeof row === 'object')
    .map((row: any) => ({
      name: evidenceText(row.projectName, row.propertyName, row.name, row.project?.projectName, row.project?.name, row.property?.name, row.propertyId?.name),
      location: evidenceText(row.location, row.microMarket, row.microMarket?.micromarketName, row.location?.name),
      reference: transactionDate(evidenceText(row.referenceDate, row.date, row.reference, row.source)),
      transactions: (Array.isArray(row.transactions) ? row.transactions : Array.isArray(row.lastFiveTransactions) ? row.lastFiveTransactions : [])
        .filter((transaction: any) => transaction && typeof transaction === 'object')
        .map((transaction: any) => mapTransaction(transaction, true)),
      psf: money(row.benchmarkPsf ?? row.psf ?? row.ratePerSqft ?? row.pricePerSqft ?? row.price),
    }));
  if (comparisonBased) {
    const average = ci.averagePsfOfSimilarProperties ?? ci.pricing?.averagePsfOfSimilarProperties ?? ci.last5TransactionsAvgPsf;
    if (average != null) report.pricing.push({ label: 'Average PSF of Similar Properties', value: typeof average === 'number' ? money(average) : String(average) });
    if (!report.comparableProjects?.length) {
      report.comparableProjects = (ci.lastFiveTransactions ?? []).filter((row: any) => row?.source && !['Property transaction', 'Similar listed property', 'Anchor property', 'Land rate benchmark'].includes(row.source) && Number(row.price) > 0)
        .map((row: any) => ({ name: evidenceText(row.projectName, row.propertyName, row.name, row.source), location: evidenceText(row.location, row.microMarket), reference: transactionDate(row.referenceDate ?? row.date), psf: money(row.benchmarkPsf ?? row.psf ?? row.price) }));
    }
    report.transactions = [];
    report.pricing = report.pricing.filter(metric => !['Last Transacted PSF', 'Last 5 Transactions Avg PSF', 'Last Transacted Date'].includes(metric.label));
  }

  const scores = ci.investmentScoreBreakdown ?? ci.investmentScores ?? {};
  report.investmentScores = [
    ['Price Score', 'priceScore'], ['Yield Score', 'yieldScore'], ['Market Score', 'marketScore'],
    ['UDS Score', 'udsScore'], ['Liquidity Score', 'liquidityScore'],
  ].flatMap(([label, key]) => {
    const value = scores[key];
    if (value == null || (typeof value === 'string' && !value.trim()) || typeof value === 'boolean') return [];
    const score = Number(value);
    if (!Number.isFinite(score) || score < 0 || score > 100) return [];
    if (label === 'UDS Score' && (propertyType === 'Plots' || propertyType === 'Independent House')) return [];
    if (label === 'Yield Score' && propertyType === 'Plots') return [];
    return [{ label, score }];
  });
  const wealth = ci.wealthProjection ?? {};
  report.investmentWealth = [
    ['Current CI Value', 'currentCiValue'], ['Potential Savings', 'potentialSavings'],
  ].flatMap(([label, key]) => {
    const value = wealth[key];
    if (value == null || value === '') return [];
    const display = typeof value === 'number' ? money(value) : typeof value === 'string' ? value : 'Not available';
    return ['', 'not available', 'n/a', '—', '-'].includes(display.trim().toLowerCase()) ? [] : [{ label, value: display }];
  });
  const investor = ci.investorIntelligence ?? ci.investment ?? ci.valuation ?? ci;
  report.investorIntelligence = [
    ['Exit Potential', 'exitPotential'],
    ['Capital Appreciation', 'capitalAppreciation'],
    ['Liquidity Outlook', 'liquidityOutlook'],
    ['Rental Growth', 'rentalGrowth'],
    ['5-Year IRR', 'fiveYearIrr'],
  ].flatMap(([label, key]) => {
    const value = investor[key];
    if (value == null || value === '' || value === 'Not available'
      || !['string', 'number'].includes(typeof value)) return [];
    if (propertyType === 'Plots' && key === 'rentalGrowth') return [];
    return [{ label, value: String(value) }];
  });
  const landReference = (Array.isArray(ci.lastFiveTransactions) ? ci.lastFiveTransactions : [])
    .find((row: any) => row?.source === 'Land rate benchmark' && Number(row.price) > 0);
  const landOnly = !!landReference && !report.transactions?.length;
  if (landReference && !report.pricing.some(metric => metric.label === 'Land Rate PSF')) {
    report.pricing.push({ label: 'Land Rate PSF', value: money(landReference.price) });
  }
  if (landOnly || (typeof ci.dateOfLastTransactedPrice === 'string' && ci.dateOfLastTransactedPrice.startsWith('Land rate benchmark'))) {
    report.pricing = report.pricing.filter(metric => !['Last Transacted PSF', 'Last 5 Transactions Avg PSF', 'Last Transacted Date'].includes(metric.label));
  }
  const available = (metric: CiMetric) => !['', 'not available', 'n/a', '—', '-'].includes(metric.value.trim().toLowerCase());
  const landProperty = propertyType === 'Plots' || propertyType === 'Independent House';
  report.pricing = report.pricing.filter(metric => available(metric) && !(landProperty && metric.label.includes('UDS')));
  report.valuation = report.valuation.filter(metric => available(metric)
    && !(landProperty && metric.label.includes('UDS'))
    && !(propertyType === 'Plots' && /Rent|Yield/.test(metric.label)));
  report.offers = report.offers.filter(available);
  if (landProperty) {
    // Land and building amounts must come from their own fields, never apartment UDS values.
    const pricing = ci.pricing ?? ci;
    const valuation = ci.valuation ?? ci;
    const add = (metrics: CiMetric[], label: string, value: unknown, formatted: boolean) => {
      const display = formatted ? (value == null || value === '' ? 'Not available' : String(value)) : money(value);
      if (display !== 'Not available') metrics.push({ label, value: display });
    };
    if (!report.pricing.some(metric => metric.label === 'Land Rate PSF')) add(report.pricing, 'Land Rate PSF', pricing.landRatePsf ?? pricing.currentLandRatePerSqft, !!ci.pricing);
    add(report.valuation, 'Land Value', valuation.landValue ?? valuation.adjustedLandValue, !!ci.valuation);
    if (propertyType === 'Independent House') {
      add(report.valuation, 'Building Value', valuation.buildingValue ?? valuation.depreciatedBuildingValue, !!ci.valuation);
      add(report.valuation, 'Land + Building Value', valuation.landAndBuildingValue, !!ci.valuation);
    }
    if (report.analysis) report.analysis = { ...report.analysis, uds: undefined,
      annualRent: propertyType === 'Plots' ? undefined : report.analysis.annualRent };
  }

  report.verdict = ci.verdict?.label ?? ci.verdict?.title ?? ci.circledIntelligenceVerdict ?? ci.adjustedVerdict ?? report.tags[0];
  const rating = (value: unknown): string | undefined => typeof value === 'string' && /^(High|Moderate|Low)$/i.test(value.trim())
    ? value.trim()[0].toUpperCase() + value.trim().slice(1).toLowerCase() : undefined;
  report.verdict = report.verdict ? cleanCiLabel(report.verdict) : undefined;
  report.tags = report.tags.map(cleanCiLabel).filter(Boolean);
  report.rentalRating = rating(ci.valuation?.rentalYieldLabel ?? ci.valuation?.rentalStrength ?? ci.rentalYieldLabel ?? ci.rentalStrength);
  report.negotiationRating = rating(ci.offerGuidance?.negotiationPotential ?? ci.negotiationPotential);
  const askingValue = ciMoneyNumber(ci.quotedUnitPrice ?? ci.quotedPriceOfUnit ?? ci.pricing?.quotedUnitPrice);
  if (!report.negotiationRating) {
    if (landProperty && ci.landAssessment) {
      // Land and house comparisons use the backend's combined assessment.
      report.negotiationRating = negotiationPotential(askingValue, ciMoneyNumber(ci.landAssessment.assessedValue));
    } else if (!landProperty) {
      const size = ciMoneyNumber(ci.size);
      const askingPsf = ciMoneyNumber(ci.quotedPsf ?? ci.quotedPricePerSqFt ?? ci.pricing?.quotedPsf)
        ?? (askingValue != null && size != null && size > 0 ? askingValue / size : undefined);
      report.negotiationRating = negotiationPotential(askingPsf, ciMoneyNumber(ci.revisedBenchmark ?? ci.pricing?.revisedBenchmarkPsf));
    }
  }
  if (propertyType !== 'Plots' && !report.valuation.some(metric => metric.label === 'Rental Yield')) {
    const monthly = ciMoneyNumber(ci.averageRentPerMonth ?? ci.valuation?.avgRentMonth);
    const annual = ciMoneyNumber(ci.averageRentPa ?? ci.valuation?.avgRentPA)
      ?? (monthly != null && monthly >= 0 ? monthly * 12 : undefined);
    // Formula & Rules!C3:C4: annualise actual rent, then divide by asking value.
    if (annual != null && annual >= 0 && askingValue != null && askingValue > 0) {
      report.valuation.push({ label: 'Rental Yield', value: percent(annual / askingValue) });
      if (!report.valuation.some(metric => metric.label === 'Annual Rent')) report.valuation.push({ label: 'Annual Rent', value: money(annual) });
    }
  }
  // Internal analystNotes may quote confidential supply override reasons.
  report.analystNote = ci.publicAnalystNotes ?? ci.customerAnalystNote;
  const metricValue = (label: string, value: unknown, format = money): CiMetric[] => {
    const display = format(value);
    return display === 'Not available' ? [] : [{ label, value: display }];
  };
  const areaValue = (value: unknown) => value != null && value !== '' && Number.isFinite(Number(value))
    ? Number(value).toLocaleString('en-IN') + ' sq.ft.' : 'Not available';
  const a = ci.landAssessment;
  if (landProperty) {
    report.transactions = [];
    report.comparableProjects = [];
    report.directTransactionHistoryLimited = false;
    report.pricing = [];
    report.valuation = report.valuation.filter(metric => /Rent|Yield/.test(metric.label));
    report.assessmentMetrics = a ? [
      ...metricValue('Plot Area', a.plotArea, areaValue),
      ...metricValue('Base Land Rate PSF', a.landRatePsf),
      ...metricValue('Adjusted Land Rate PSF', a.adjustedLandRatePsf ?? (Number(a.plotArea) > 0 && a.adjustedLandValue != null ? Number(a.adjustedLandValue) / Number(a.plotArea) : undefined)),
      ...metricValue('Assessed Land Value', a.finalLandValue),
      ...(propertyType === 'Independent House' ? [
        ...metricValue('Built-up Area', a.builtUpArea, areaValue),
        ...metricValue('Construction Rate PSF', a.buildingRatePsf),
        ...metricValue('Building Value', a.totalBuildingValue),
        ...metricValue('Total Assessed Property Value', a.assessedValue),
      ] : []),
      ...metricValue(propertyType === 'Plots' ? 'Ask vs Assessed Land Value' : 'Ask vs Total Assessed Property Value', a.askVsCiPercent, percent),
    ] : [
      ...metricValue('Assessed Land Value', typeof ci.landValue === 'number' ? ci.landValue : ci.valuation?.landValue ?? ci.adjustedLandValue, value => typeof value === 'string' ? value : money(value)),
      ...(propertyType === 'Independent House' ? [
        ...metricValue('Building Value', typeof ci.buildingValue === 'number' ? ci.buildingValue : ci.valuation?.buildingValue ?? ci.depreciatedBuildingValue, value => typeof value === 'string' ? value : money(value)),
        ...metricValue('Total Assessed Property Value', ci.landAndBuildingValue ?? ci.valuation?.landAndBuildingValue),
      ] : []),
    ];
    report.analysis = { ...report.analysis, fairValue: a?.assessedValue, difference: a?.askVsCiPercent, uds: undefined };
    if (a) {
      report.adjustmentBreakdown = {
        base: { label: 'Base Land Rate PSF', value: money(a.landRatePsf) },
        final: { label: 'Adjusted Land Rate PSF', value: money(a.adjustedLandRatePsf ?? (Number(a.plotArea) > 0 && a.adjustedLandValue != null ? Number(a.adjustedLandValue) / Number(a.plotArea) : undefined)) },
        total: a.totalLandAdjustment == null ? undefined : percent(a.totalLandAdjustment),
        rows: [
          { label: 'Facing', value: a.orientationAdjustment, reason: a.orientation },
          { label: 'Road Width', value: a.roadAdjustment, reason: a.roadWidthFt != null ? `${a.roadWidthFt} ft road` : undefined },
          { label: 'Premium Plot', value: a.premiumAdjustment, reason: ci.landAdjustmentReasons?.premiumPlot },
        ].filter(row => row.value != null && Number(row.value) !== 0)
          .map(row => ({ label: row.label, percentage: percent(row.value), reason: row.reason ?? undefined })),
      };
    }
  } else if (ci.checklistWeightages || ci.totalChecklistWeightagePercent != null) {
    const weightages = ci.checklistWeightages ?? {};
    report.adjustmentBreakdown = {
      base: { label: 'Base Benchmark PSF', value: money(ci.benchmarkPsf) },
      final: { label: 'Final Adjusted Benchmark PSF', value: money(ci.revisedBenchmark) },
      total: ci.totalChecklistWeightagePercent == null ? undefined : percent(ci.totalChecklistWeightagePercent),
      rows: [
        { key: 'locality', label: 'Location', reason: ci.locality },
        { key: 'builderTier', label: 'Builder', reason: ci.builderTier },
        { key: 'legalIssue', label: 'Legal Status', reason: ci.legalIssueStatus },
        { key: 'scarcity', label: 'Scarcity', reason: ci.scarcity },
        { key: 'samePropertyOverpricing', label: 'Pricing', reason: ci.samePropertyOverpricing },
      ].filter(row => weightages[row.key] != null && Number(weightages[row.key]) !== 0)
        .map(row => ({ label: row.label, percentage: percent(weightages[row.key]), reason: row.reason ?? undefined })),
    };
  }
  return report;
}
